import { supabase } from '@/lib/supabase/client';
import { UserProfile, UpdateUserProfileRequest } from '@/types/user';

export const userProfileService = {
  /**
   * Get the current user's profile
   */
  async getUserProfile(): Promise<UserProfile> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }
    
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') {
        // Profile doesn't exist yet, create a default one
        return this.createDefaultUserProfile(user.id, user.email || '', user.user_metadata?.full_name);
      }
      throw new Error(`Failed to fetch user profile: ${error.message}`);
    }
    
    return data;
  },
  
  /**
   * Create a default user profile
   */
  async createDefaultUserProfile(userId: string, email: string, fullName?: string): Promise<UserProfile> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    
    const defaultProfile = {
      id: userId,
      email: email,
      full_name: fullName || email.split('@')[0], // Use part before @ in email if no name is provided
      notification_preferences: {
        email_notifications: true,
        schedule_updates: true,
        shift_trade_requests: true,
        reminder_notifications: true,
        advance_reminder_days: 3
      }
    };
    
    const { data, error } = await supabase
      .from('user_profiles')
      .insert(defaultProfile)
      .select('*')
      .single();
    
    if (error) {
      throw new Error(`Failed to create user profile: ${error.message}`);
    }
    
    return data;
  },
  
  /**
   * Update the current user's profile
   */
  async updateUserProfile(request: UpdateUserProfileRequest): Promise<UserProfile> {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }
    
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('Not authenticated');
    }
    
    // First get the current profile
    const currentProfile = await this.getUserProfile();
    
    // Prepare the update data
    const updateData: Partial<UserProfile> = {};
    
    if (request.full_name !== undefined) {
      updateData.full_name = request.full_name;
    }
    
    if (request.phone_number !== undefined) {
      updateData.phone_number = request.phone_number;
    }
    
    if (request.job_title !== undefined) {
      updateData.job_title = request.job_title;
    }
    
    if (request.department !== undefined) {
      updateData.department = request.department;
    }
    
    if (request.notification_preferences) {
      updateData.notification_preferences = {
        ...currentProfile.notification_preferences,
        ...request.notification_preferences
      };
    }
    
    // Update the user metadata in the auth system for the name
    if (request.full_name !== undefined) {
      await supabase.auth.updateUser({
        data: { full_name: request.full_name }
      });
    }
    
    // Update the profile in the database
    const { data, error } = await supabase
      .from('user_profiles')
      .update(updateData)
      .eq('id', user.id)
      .select('*')
      .single();
    
    if (error) {
      throw new Error(`Failed to update user profile: ${error.message}`);
    }
    
    return data;
  }
}; 